import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BrandLogo from '@/components/BrandLogo';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { Loader2, Check, Menu, X, Monitor, LogIn } from 'lucide-react';
import Footer from '@/components/Footer';
import axios from 'axios';
import TermsConditionsPage from './TermsConditionsPage';
import PrivacyPolicyPage from './PrivacyPolicyPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestDemoPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    organization_name: '',
    number_of_beds: '',
    number_of_staff: '',
    terms_accepted: false
  });

  const bedOptions = [
    'Up to 50',
    'Up to 100',
    'Up to 200',
    'Up to 300',
    'Up to 500',
    'Up to 1000',
    'Up to 2000',
    'Above 2000'
  ];

  const staffOptions = [
    'Up to 50',
    'Up to 100',
    'Up to 200',
    'Up to 300',
    'Up to 500',
    'Up to 1000',
    'Up to 2000',
    'Above 2000'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      terms_accepted: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name) {
      toast.error('Please enter your full name.');
      return;
    }
    if (!formData.phone_number) {
      toast.error('Please enter your phone number.');
      return;
    }
    if (!formData.email) {
      toast.error('Please enter your email address.');
      return;
    }
    if (!formData.organization_name) {
      toast.error('Please enter your organization name.');
      return;
    }
    if (!formData.number_of_beds) {
      toast.error('Please select number of beds.');
      return;
    }
    if (!formData.number_of_staff) {
      toast.error('Please select number of staff.');
      return;
    }
    if (!formData.terms_accepted) {
      toast.error('Please accept the Terms & conditions and Privacy policy.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/public/request-demo`, {
        ...formData,
        institution_name: formData.organization_name
      });
      
      toast.success('Demo request submitted successfully! Our team will contact you soon.');
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit demo request. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header/Navigation */}
      <header className="bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <BrandLogo />
            </div>

            {/* Desktop Navigation & Buttons - All aligned right with equal spacing */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/solutions')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                The solution
              </button>
              <button onClick={() => navigate('/pricing')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Contact us
              </button>
              <Button 
                onClick={() => navigate('/request-demo')}
                className="bg-[#10B981] hover:bg-[#059669] text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Request for demo
              </Button>
              <Button 
                onClick={() => navigate('/signin')}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3 border-t border-gray-200 pt-4">
              <button 
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Home
              </button>
              <button 
                onClick={() => { navigate('/solutions'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                The solution
              </button>
              <button 
                onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => { navigate('/contact'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Contact us
              </button>
              <div className="space-y-2 pt-2">
                <Button 
                  onClick={() => { navigate('/request-demo'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white"
                >
                  Request for demo
                </Button>
                <Button 
                  onClick={() => { navigate('/signin'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#0056B3] hover:bg-[#004494] text-white"
                >
                  Sign in
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Two Column Layout */}
      <main className="flex-1 grid md:grid-cols-2">
        {/* Left Side - Image */}
        <div className="hidden md:block relative bg-gradient-to-br from-blue-50 to-blue-100">
          <img
            src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1000&h=1000&fit=crop"
            alt="Healthcare Technology"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20"></div>
        </div>

        {/* Right Side - Form */}
        <div className="flex items-center justify-center p-6 md:p-12 bg-white">
          <div className="w-full max-w-2xl">
            {/* Form Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Request for product demo
              </h1>
              <p className="text-gray-600">
                Fill out the form below to get in touch with us today!
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Row 1: Full Name and Phone Number */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">
                    Full name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    type="text"
                    placeholder="Enter your name here"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number">
                    Phone number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone_number"
                    name="phone_number"
                    type="tel"
                    placeholder="Enter your phone number here"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Row 2: Email and Organization Name */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email address here"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organization_name">
                    Organization name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="organization_name"
                    name="organization_name"
                    type="text"
                    placeholder="Enter the name of your organization here"
                    value={formData.organization_name}
                    onChange={handleInputChange}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Row 3: Number of Beds and Staff */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="number_of_beds">
                    Number of beds <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.number_of_beds}
                    onValueChange={(value) => handleSelectChange('number_of_beds', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select number of beds" />
                    </SelectTrigger>
                    <SelectContent>
                      {bedOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="number_of_staff">
                    Number of staff <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.number_of_staff}
                    onValueChange={(value) => handleSelectChange('number_of_staff', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms_accepted"
                  checked={formData.terms_accepted}
                  onCheckedChange={handleCheckboxChange}
                  className="mt-1"
                />
                <label htmlFor="terms_accepted" className="text-sm text-gray-700 leading-tight">
                  I accept the{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTermsModal(true);
                    }}
                    className="text-blue-600 hover:underline cursor-pointer"
                  >
                    Terms & conditions
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPrivacyModal(true);
                    }}
                    className="text-blue-600 hover:underline cursor-pointer"
                  >
                    Privacy policy
                  </button>{' '}
                  <span className="text-red-500">*</span>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Submit request
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Terms & Conditions Modal */}
      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Terms & Conditions</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 mt-4">
            <TermsConditionsPage isModal={true} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Modal */}
      <Dialog open={showPrivacyModal} onOpenChange={setShowPrivacyModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Privacy Policy</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 mt-4">
            <PrivacyPolicyPage isModal={true} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestDemoPage;
